import * as React from 'react'
import { ChevronDown, ChevronUp, Pencil, Plus, Table2, Trash2 } from 'lucide-react'
import { sb } from '@/lib/supabase'
import type {
  ReportSection,
  ReportSectionKind,
  ReportSectionPlacement,
  ReportTableData,
} from '@/lib/types'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Markdown } from '@/components/page'
import { useDirty, useDiscardGuard } from '@/hooks/use-unsaved'
import { DiscardPrompt } from '@/components/discard-prompt'

/**
 * Coach-authored report sections: the parts of a real analysis that aren't
 * generated from the month's data — method notes, survey findings, a schedule,
 * a strategy, next steps.
 *
 * Editing happens in place rather than inside the report editor's form, because
 * a section only makes sense where it sits: you add "Q3 Theme" between the
 * summary and the metrics, not in a list of fields somewhere else. Every control
 * carries `no-print` so the PDF is the document alone (see index.css).
 */
export function ReportSectionBlocks({
  reportId,
  placement,
  sections,
  canEdit,
  onChanged,
}: {
  reportId: string
  placement: ReportSectionPlacement
  sections: ReportSection[]
  canEdit: boolean
  onChanged: () => Promise<void>
}) {
  const [editing, setEditing] = React.useState<ReportSection | null>(null)
  const [adding, setAdding] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  const mine = sections
    .filter((s) => s.placement === placement)
    .sort((a, b) => a.display_order - b.display_order || a.created_at.localeCompare(b.created_at))

  async function remove(section: ReportSection) {
    if (!window.confirm(`Delete the “${section.heading || 'untitled'}” section?`)) return
    const { error: err } = await sb().from('report_sections').delete().eq('id', section.id)
    if (err) return setError(err.message)
    await onChanged()
  }

  // Swap display_order with the neighbour. Two updates rather than a
  // renumbering pass: the list is short, and a swap can't leave a gap.
  async function move(section: ReportSection, delta: -1 | 1) {
    const i = mine.findIndex((s) => s.id === section.id)
    const other = mine[i + delta]
    if (!other) return
    const [a, b] = [section.display_order, other.display_order]
    const results = await Promise.all([
      sb().from('report_sections').update({ display_order: b }).eq('id', section.id),
      sb().from('report_sections').update({ display_order: a }).eq('id', other.id),
    ])
    const failed = results.find((r) => r.error)
    if (failed?.error) return setError(failed.error.message)
    await onChanged()
  }

  if (mine.length === 0 && !canEdit) return null

  return (
    <>
      {error && <p className="no-print mt-3 text-sm text-destructive">{error}</p>}

      {mine.map((section, i) => (
        <section key={section.id} className="report-section mt-8">
          <div className="flex items-start gap-2 border-b pb-1.5">
            <h2 className="min-w-0 flex-1 text-lg font-semibold text-primary">
              {section.heading || 'Untitled section'}
            </h2>
            {canEdit && (
              <div className="no-print flex shrink-0 items-center">
                <Button
                  variant="ghost"
                  size="icon-sm"
                  disabled={i === 0}
                  onClick={() => void move(section, -1)}
                  aria-label="Move section up"
                >
                  <ChevronUp />
                </Button>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  disabled={i === mine.length - 1}
                  onClick={() => void move(section, 1)}
                  aria-label="Move section down"
                >
                  <ChevronDown />
                </Button>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => setEditing(section)}
                  aria-label={`Edit ${section.heading || 'section'}`}
                >
                  <Pencil />
                </Button>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => void remove(section)}
                  aria-label={`Delete ${section.heading || 'section'}`}
                >
                  <Trash2 />
                </Button>
              </div>
            )}
          </div>

          <div className="mt-3 space-y-3">
            {section.body?.trim() && <Markdown source={section.body} />}
            {section.kind === 'table' && <SectionTable data={section.data} />}
          </div>
        </section>
      ))}

      {canEdit && (
        <div className="no-print mt-4">
          <Button variant="outline" size="sm" onClick={() => setAdding(true)}>
            <Plus /> Add section {placement === 'before_data' ? 'before' : 'after'} the data
          </Button>
        </div>
      )}

      {(adding || editing) && (
        <SectionDialog
          reportId={reportId}
          placement={placement}
          section={editing ?? undefined}
          nextOrder={mine.length > 0 ? Math.max(...mine.map((s) => s.display_order)) + 1 : 0}
          onClose={() => {
            setAdding(false)
            setEditing(null)
          }}
          onSaved={async () => {
            setAdding(false)
            setEditing(null)
            await onChanged()
          }}
        />
      )}
    </>
  )
}

function SectionTable({ data }: { data: ReportSection['data'] }) {
  const table = asTable(data)
  if (!table || table.rows.length === 0) return null
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b text-left text-xs text-muted-foreground">
            {table.columns.map((c, i) => (
              <th key={i} className="pb-2 pr-4 font-medium last:pr-0">
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {table.rows.map((row, r) => (
            <tr key={r} className="border-b last:border-0">
              {table.columns.map((_, c) => (
                <td key={c} className="py-2 pr-4 align-top last:pr-0">
                  {row[c] ?? ''}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/** Narrows the jsonb column, which is `{}` for a text section. */
export function asTable(data: ReportSection['data']): ReportTableData | null {
  if (!data || !('columns' in data) || !Array.isArray(data.columns)) return null
  const rows = Array.isArray((data as ReportTableData).rows) ? (data as ReportTableData).rows : []
  return { columns: (data as ReportTableData).columns.map(String), rows }
}

function SectionDialog({
  reportId,
  placement,
  section,
  nextOrder,
  onClose,
  onSaved,
}: {
  reportId: string
  placement: ReportSectionPlacement
  section?: ReportSection
  nextOrder: number
  onClose: () => void
  onSaved: () => Promise<void>
}) {
  const [heading, setHeading] = React.useState(section?.heading ?? '')
  const [kind, setKind] = React.useState<ReportSectionKind>(section?.kind ?? 'text')
  const [body, setBody] = React.useState(section?.body ?? '')
  const [raw, setRaw] = React.useState(() => tableToText(asTable(section?.data ?? {})))
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  const dirty = useDirty({ heading, kind, body, raw })
  const guard = useDiscardGuard(dirty, onClose)

  const parsed = React.useMemo(() => (kind === 'table' ? parseTable(raw) : null), [kind, raw])

  async function save(e: React.FormEvent) {
    e.preventDefault()
    if (!heading.trim()) return setError('Give the section a heading.')
    if (kind === 'table' && (!parsed || parsed.rows.length === 0)) {
      return setError('Paste or type at least a header row and one row of data.')
    }

    const row = {
      heading: heading.trim(),
      kind,
      body: body.trim() || null,
      data: kind === 'table' ? parsed : {},
    }

    setBusy(true)
    setError(null)
    const { error: err } = section
      ? await sb().from('report_sections').update(row).eq('id', section.id)
      : await sb()
          .from('report_sections')
          .insert({ ...row, report_id: reportId, placement, display_order: nextOrder })
    setBusy(false)
    if (err) return setError(err.message)
    await onSaved()
  }

  return (
    <Dialog open onOpenChange={(o) => !o && guard.requestClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DiscardPrompt guard={guard} what="this section" />
        <form onSubmit={save}>
          <DialogHeader>
            <DialogTitle>{section ? 'Edit section' : 'New section'}</DialogTitle>
            <DialogDescription>
              Sections sit {placement === 'before_data' ? 'between the summary and the metrics' : 'between the assessment and the conclusion'}. They&apos;re yours to write — nothing
              regenerates them, so they aren&apos;t frozen when you publish.
            </DialogDescription>
          </DialogHeader>

          <div className="my-4 space-y-4">
            <div className="grid gap-3 sm:grid-cols-[1fr_180px]">
              <div className="space-y-2">
                <Label htmlFor="sheading">Heading</Label>
                <Input
                  id="sheading"
                  autoFocus
                  value={heading}
                  onChange={(e) => setHeading(e.target.value)}
                  placeholder="Survey Findings"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="skind">Kind</Label>
                <Select value={kind} onValueChange={(v) => setKind(v as ReportSectionKind)}>
                  <SelectTrigger id="skind">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="text">Text</SelectItem>
                    <SelectItem value="table">Table</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="sbody">{kind === 'table' ? 'Lead-in (optional)' : 'Body'}</Label>
              <Textarea
                id="sbody"
                rows={kind === 'table' ? 3 : 10}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder={
                  kind === 'table'
                    ? 'A sentence or two introducing the table.'
                    : 'Markdown works: **bold**, lists, headings.'
                }
              />
            </div>

            {kind === 'table' && (
              <div className="space-y-2">
                <Label htmlFor="stable">Table data</Label>
                <p className="text-xs text-muted-foreground">
                  Paste straight from a spreadsheet or a document — tab, comma, or{' '}
                  <code className="text-[0.85em]">|</code> separated. The first row is the header.
                </p>
                <Textarea
                  id="stable"
                  rows={9}
                  value={raw}
                  onChange={(e) => setRaw(e.target.value)}
                  placeholder={'Week\tDate\tTopic\tLead\n1\tJul 20\tMetrics\tChad'}
                  className="font-mono text-xs"
                />
                {parsed && parsed.rows.length > 0 ? (
                  <p className="text-xs text-muted-foreground">
                    {parsed.columns.length} columns · {parsed.rows.length}{' '}
                    {parsed.rows.length === 1 ? 'row' : 'rows'} — {parsed.columns.join(' · ')}
                  </p>
                ) : (
                  <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Table2 className="h-3.5 w-3.5" /> Nothing parsed yet.
                  </p>
                )}
              </div>
            )}

            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={guard.requestClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy}>
              {section ? 'Save section' : 'Add section'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Turn pasted text into columns + rows.
 *
 * Deliberately forgiving about the separator, because the sources are a
 * spreadsheet (tabs), a CSV export (commas), and a markdown table copied out of
 * a document (pipes) — asking which one you have is a worse experience than
 * detecting it per line. Markdown's `|---|---|` rule row is dropped rather than
 * becoming a row of dashes.
 */
export function parseTable(text: string): ReportTableData | null {
  const lines = text
    .replace(/\r\n/g, '\n')
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean)
    .filter((l) => !/^\|?\s*:?-{2,}/.test(l))
  if (lines.length === 0) return null

  const cells = lines.map((line) => {
    if (line.includes('\t')) return line.split('\t').map((c) => c.trim())
    if (line.includes('|')) {
      return line
        .replace(/^\||\|$/g, '')
        .split('|')
        .map((c) => c.trim())
    }
    return line.split(',').map((c) => c.trim())
  })

  const columns = cells[0]
  const width = columns.length
  const rows = cells.slice(1).map((r) => {
    const padded = r.slice(0, width)
    while (padded.length < width) padded.push('')
    return padded
  })
  return { columns, rows }
}

/** The inverse, so editing a saved table shows what you'd paste. */
function tableToText(table: ReportTableData | null): string {
  if (!table) return ''
  return [table.columns, ...table.rows].map((r) => r.join('\t')).join('\n')
}
